import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Title,
  Card,
  Text,
  Stack,
  Group,
  Loader,
  Alert,
  Button,
  Pagination,
  ThemeIcon,
  UnstyledButton,
  Box
} from '@mantine/core';
import { notifications as toast } from '@mantine/notifications';
import {
  IconExclamationCircle,
  IconBell,
  IconCoin,
  IconHeart,
  IconStar,
  IconSparkles,
  IconRocket,
  IconGift
} from '@tabler/icons-react';
import { useApiClient, useLiveQuery, useSession } from '../api';
import { formatCents } from '../components/products/productDisplay';
import ProtectedRoute from '../components/auth/ProtectedRoute';

const PAGE_LIMIT = 20;

// type → { icon, color, sentence, link } (contract §4.11). Unknown future
// types fall through to a generic entry rather than crashing.
const describeNotification = (notification) => {
  const data = notification.data || {};
  switch (notification.type) {
    case 'seller.favorited':
      return {
        icon: IconHeart,
        color: 'pink',
        text: 'Someone added you to their favorites',
        to: null,
      };
    case 'review.created':
      return {
        icon: IconStar,
        color: 'yellow',
        text: `You received a new ${data.rating}-star review`,
        to: data.seller_id != null ? `/princess/${data.seller_id}` : null,
      };
    case 'favorite.posted':
      return {
        icon: IconSparkles,
        color: 'grape',
        text: `${data.seller_display_name} posted a new ${data.kind === 'product' ? 'product' : 'post'}`,
        to: data.seller_id != null ? `/princess/${data.seller_id}` : null,
      };
    case 'tip.received':
      return {
        icon: IconCoin,
        color: 'yellow',
        text: `You received a ${formatCents(data.amount_cents)} tip`,
        to: null,
      };
    case 'boost.activated':
      // §4.21 — the CCBill webhook completed a boost purchase.
      return {
        icon: IconRocket,
        color: 'yellow',
        text: data.featured_until
          ? `Your profile boost is active — featured until ${new Date(data.featured_until).toLocaleDateString()}`
          : 'Your profile boost is active',
        to: null,
      };
    case 'boost.reversed':
      // §4.20/§4.21 — a boost payment was refunded / charged back.
      return {
        icon: IconRocket,
        color: 'red',
        text: 'A boost payment was reversed — featured time removed',
        to: null,
      };
    case 'referral.attached':
      // §4.22 — someone attached via the user's referral link (anonymous).
      return {
        icon: IconGift,
        color: 'teal',
        text: 'Someone joined through your referral link',
        to: '/referrals',
      };
    case 'earning.reversed':
      // §4.20 clawback — payer-anonymous, like every money notification.
      return {
        icon: IconCoin,
        color: 'red',
        text: `A payment was reversed (refund/chargeback): −${formatCents(data.amount_cents)}`,
        to: null,
      };
    default:
      return {
        icon: IconBell,
        color: 'gray',
        text: 'You have a new notification',
        to: null,
      };
  }
};

const formatNotificationTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

const NotificationItem = ({ notification, onClick }) => {
  const { icon: Icon, color, text } = describeNotification(notification);
  const unread = !notification.read_at;

  return (
    <UnstyledButton
      onClick={() => onClick(notification)}
      w="100%"
      p="md"
      style={{
        background: unread ? 'var(--mantine-color-pink-0)' : 'transparent',
        borderRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Group gap="md" wrap="nowrap">
        <ThemeIcon variant="light" color={color} size="lg" radius="xl">
          <Icon size="1.1rem" />
        </ThemeIcon>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={unread ? 600 : 400}>
            {text}
          </Text>
          <Text size="xs" c="dimmed">
            {formatNotificationTime(notification.created_at)}
          </Text>
        </Box>
        {unread && (
          <Box
            data-testid={`unread-dot-${notification.id}`}
            w={8}
            h={8}
            style={{
              borderRadius: '50%',
              background: 'var(--mantine-color-pink-6)',
              flexShrink: 0,
            }}
          />
        )}
      </Group>
    </UnstyledButton>
  );
};

const NotificationsPage = () => {
  const [page, setPage] = useState(1);
  const [markingAll, setMarkingAll] = useState(false);
  const { isSignedIn } = useAuth();
  const { me } = useSession();
  const api = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mark-read emits no realtime event (contract §4.11), and the header
  // bell's { limit: 1 } query shares this path but not this query key —
  // invalidate the path prefix so the list AND the unread badge refresh.
  const refreshNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/v1/me/notifications'] });
  };

  // Newest first (contract §4.11); the notifications topic keeps the list live.
  const { data, loading, error } = useLiveQuery(
    '/api/v1/me/notifications',
    { page, limit: PAGE_LIMIT },
    {
      enabled: !!isSignedIn,
      topics: [me && `user.${me.clerk_user_id}.notifications`],
      query: { placeholderData: (prev) => prev },
    }
  );
  const items = data?.items ?? [];
  const unreadCount = data?.unread_count ?? 0;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const handleItemClick = async (notification) => {
    const { to } = describeNotification(notification);
    if (!notification.read_at) {
      try {
        await api.post('/api/v1/me/notifications/read', { ids: [notification.id] });
        refreshNotifications();
      } catch {
        // Non-fatal — still navigate; the item stays unread for next time.
      }
    }
    if (to) navigate(to);
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post('/api/v1/me/notifications/read', { all: true });
      refreshNotifications();
    } catch (err) {
      toast.show({
        title: 'Error',
        message: err?.message || 'Could not mark notifications as read',
        color: 'red',
      });
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="sm" py="xl">
        <Stack gap="xl">
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <IconBell size="1.5rem" color="var(--mantine-color-pink-6)" />
              <Title order={1}>Notifications</Title>
            </Group>
            <Button
              variant="light"
              size="xs"
              disabled={unreadCount === 0}
              loading={markingAll}
              onClick={handleMarkAllRead}
            >
              Mark all as read
            </Button>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : items.length === 0 ? (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack align="center" gap="md" py="xl">
                <IconBell size={48} color="var(--mantine-color-gray-4)" />
                <Text c="dimmed" size="lg">No notifications yet</Text>
                <Text c="dimmed" size="sm">
                  You&apos;re all caught up.
                </Text>
              </Stack>
            </Card>
          ) : (
            <Card shadow="sm" padding="xs" radius="md" withBorder>
              <Stack gap={4}>
                {items.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={handleItemClick}
                  />
                ))}
              </Stack>
            </Card>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </Stack>
      </Container>
    </ProtectedRoute>
  );
};

export default NotificationsPage;
